/**
 * BlogGenerator — SEO blog post generation, approval, and Shopify publishing.
 */
import React, { useState, useEffect, useCallback } from "react";

const CATEGORY_LABELS = {
  product_guides:   "Product Guides",
  how_to:           "How-To",
  trends:           "Trends",
  salon_business:   "Salon Business",
  brand_stories:    "Brand Stories",
  comparison_safe:  "Comparisons",
};

const CATEGORY_ICONS = {
  product_guides:  "📦",
  how_to:          "🎓",
  trends:          "📈",
  salon_business:  "💼",
  brand_stories:   "⭐",
  comparison_safe: "⚖️",
};

function BlogPostCard({ post, onApprove, onReject, onPublish, publishing }) {
  const [expanded, setExpanded]     = useState(false);
  const [showHtml, setShowHtml]     = useState(false);

  const statusCls = {
    pending:      "seo-status-pending",
    approved:     "seo-status-approved",
    rejected:     "seo-status-rejected",
    published:    "seo-status-applied",
    saved_draft:  "seo-status-approved",
  };

  return (
    <div className="seo-blog-post-card">
      <div className="seo-blog-post-header" onClick={() => setExpanded(e => !e)}>
        <div className="seo-blog-post-header-left">
          <span className={`seo-status-badge ${statusCls[post.status] || "seo-status-pending"}`}>
            {post.status}
          </span>
          <span className="seo-blog-category-badge">
            {CATEGORY_ICONS[post.category] || "📝"} {CATEGORY_LABELS[post.category] || post.category}
          </span>
        </div>
        <div className="seo-blog-post-title">{post.title || "Untitled"}</div>
        <span className="seo-change-chevron">{expanded ? "▲" : "▼"}</span>
      </div>

      {expanded && (
        <div className="seo-blog-post-body">
          {post.meta_description && (
            <div className="seo-blog-meta-desc">
              <span className="seo-label">Meta Description: </span>
              {post.meta_description}
            </div>
          )}
          {post.focus_keyword && (
            <div className="seo-blog-focus-kw">
              <span className="seo-label">Focus Keyword: </span>
              <span className="seo-kw-tag">{post.focus_keyword}</span>
            </div>
          )}
          {post.tags?.length > 0 && (
            <div className="seo-blog-tags">
              {post.tags.map(t => <span key={t} className="seo-kw-tag">{t}</span>)}
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <button
              className="seo-btn seo-btn-ghost seo-btn-sm"
              onClick={() => setShowHtml(h => !h)}
            >
              {showHtml ? "Hide Content" : "Preview Content"}
            </button>
          </div>

          {showHtml && post.body_html && (
            <div
              className="seo-blog-preview"
              dangerouslySetInnerHTML={{ __html: post.body_html }}
            />
          )}

          <div className="seo-change-actions" style={{ marginTop: 12 }}>
            {post.status === "pending" && (
              <>
                <button
                  className="seo-btn seo-btn-primary seo-btn-sm"
                  onClick={() => onApprove(post.id)}
                >
                  ✓ Approve
                </button>
                <button
                  className="seo-btn seo-btn-danger seo-btn-sm"
                  onClick={() => onReject(post.id)}
                >
                  ✕ Reject
                </button>
              </>
            )}
            {post.status === "approved" && (
              <>
                <button
                  className="seo-btn seo-btn-green seo-btn-sm"
                  onClick={() => onPublish(post.id, true)}
                  disabled={publishing}
                >
                  {publishing ? "Publishing…" : "🚀 Publish Live"}
                </button>
                <button
                  className="seo-btn seo-btn-ghost seo-btn-sm"
                  onClick={() => onPublish(post.id, false)}
                  disabled={publishing}
                >
                  💾 Save as Draft
                </button>
              </>
            )}
            {(post.status === "published" || post.status === "saved_draft") && (
              <div className="seo-change-applied">
                {post.status === "published" ? "✅ Published to Shopify Blog" : "📄 Saved as Draft in Shopify"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function BlogGenerator() {
  const [topics, setTopics]       = useState({});
  const [posts, setPosts]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(null);
  const [result, setResult]       = useState(null);

  // Generate form state
  const [customTopic, setCustomTopic] = useState("");
  const [category, setCategory]     = useState("product_guides");
  const [wordCount, setWordCount]   = useState(1200);
  const [keywords, setKeywords]     = useState("");
  const [selectedTopic, setSelectedTopic] = useState(null);

  const [activeTab, setActiveTab] = useState("generate"); // generate | queue

  const loadTopics = useCallback(async () => {
    try {
      const r = await fetch("/api/seo/blog/topics");
      const d = await r.json();
      if (d.success) {
        const grouped = {};
        d.topics.forEach(t => {
          if (t.topics) {
            grouped[t.category] = t.topics;
          } else if (t.category && t.topic) {
            if (!grouped[t.category]) grouped[t.category] = [];
            grouped[t.category].push(t.topic);
          }
        });
        setTopics(grouped);
      }
    } catch (e) { console.error(e); }
  }, []);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/seo/blog/all");
      const d = await r.json();
      if (d.success) setPosts(d.posts || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTopics(); loadPosts(); }, [loadTopics, loadPosts]);

  const generate = async () => {
    const topic = selectedTopic || customTopic.trim();
    if (!topic) { setResult("Please select or enter a topic"); return; }
    setGenerating(true);
    setResult(null);
    try {
      const r = await fetch("/api/seo/blog/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          topic,
          category,
          word_count: wordCount,
          target_keywords: keywords ? keywords.split(",").map(k => k.trim()) : [],
        }),
      });
      const d = await r.json();
      if (d.success) {
        setResult(`Generated ${d.generated} post${d.generated !== 1 ? "s" : ""} — ready for review`);
        loadPosts();
        setActiveTab("queue");
      } else {
        setResult("Generation failed");
      }
    } catch (e) {
      setResult("Request failed");
    } finally {
      setGenerating(false);
    }
  };

  const approve = async (id) => {
    await fetch(`/api/seo/blog/approve/${id}`, { method: "POST" });
    loadPosts();
  };

  const reject = async (id) => {
    await fetch(`/api/seo/blog/reject/${id}`, { method: "POST" });
    loadPosts();
  };

  const publish = async (id, live) => {
    setPublishing(id);
    try {
      const r = await fetch("/api/seo/blog/publish", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ post_id: id, publish: live }),
      });
      const d = await r.json();
      if (d.success) {
        setResult(live ? "✅ Post published to Shopify blog!" : "📄 Saved as draft in Shopify");
        loadPosts();
      } else {
        setResult(`Publish failed: ${d.detail || "unknown error"}`);
      }
    } catch (e) {
      setResult("Publish request failed");
    } finally {
      setPublishing(null);
    }
  };

  const pendingCount = posts.filter(p => p.status === "pending").length;

  return (
    <div className="seo-blog-wrap">
      <div className="seo-blog-tabs">
        <button
          className={`seo-filter-tab ${activeTab === "generate" ? "active" : ""}`}
          onClick={() => setActiveTab("generate")}
        >
          ✍️ Generate Posts
        </button>
        <button
          className={`seo-filter-tab ${activeTab === "queue" ? "active" : ""}`}
          onClick={() => setActiveTab("queue")}
        >
          📋 Post Queue {pendingCount > 0 && <span className="seo-badge-count">{pendingCount}</span>}
        </button>
      </div>

      {result && (
        <div className="seo-toast seo-toast-ok">
          {result}
          <button className="seo-toast-close" onClick={() => setResult(null)}>✕</button>
        </div>
      )}

      {/* Generate tab */}
      {activeTab === "generate" && (
        <div className="seo-blog-generate">
          <div className="seo-blog-form-card">
            <div className="seo-blog-form-title">Configure Post</div>
            <div className="seo-form-row">
              <label className="seo-label">Category</label>
              <select
                className="seo-input"
                value={category}
                onChange={e => { setCategory(e.target.value); setSelectedTopic(null); }}
              >
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div className="seo-form-row">
              <label className="seo-label">Custom Topic (or pick below)</label>
              <input
                className="seo-input"
                placeholder="e.g. Best hair dryers for UAE salons in 2026"
                value={customTopic}
                onChange={e => { setCustomTopic(e.target.value); setSelectedTopic(null); }}
              />
            </div>
            <div className="seo-form-row">
              <label className="seo-label">Target Keywords (comma-separated)</label>
              <input
                className="seo-input"
                placeholder="e.g. hair dryer Dubai, professional salon tools UAE"
                value={keywords}
                onChange={e => setKeywords(e.target.value)}
              />
            </div>
            <div className="seo-form-row">
              <label className="seo-label">Word Count: {wordCount}</label>
              <input
                type="range" min="600" max="2500" step="100"
                value={wordCount}
                onChange={e => setWordCount(Number(e.target.value))}
                className="seo-range"
              />
            </div>
            {selectedTopic && (
              <div className="seo-selected-topic">
                Selected: <strong>{selectedTopic}</strong>
                <button className="seo-btn seo-btn-ghost seo-btn-sm" onClick={() => setSelectedTopic(null)}>✕</button>
              </div>
            )}
            <button
              className="seo-btn seo-btn-primary"
              onClick={generate}
              disabled={generating}
              style={{ marginTop: 16, width: "100%" }}
            >
              {generating ? "⟳ Generating with DeepSeek AI…" : "⚡ Generate Blog Post"}
            </button>
          </div>

          {/* Topic picker */}
          {topics[category] && (
            <div className="seo-blog-topic-grid-wrap">
              <div className="seo-blog-topic-grid-title">
                {CATEGORY_ICONS[category]} Quick Pick — {CATEGORY_LABELS[category]}
              </div>
              <div className="seo-blog-topic-grid">
                {topics[category].map((t, i) => (
                  <button
                    key={i}
                    className={`seo-topic-chip ${selectedTopic === t ? "active" : ""}`}
                    onClick={() => { setSelectedTopic(t); setCustomTopic(""); }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Queue tab */}
      {activeTab === "queue" && (
        <div className="seo-blog-queue">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button className="seo-btn seo-btn-ghost seo-btn-sm" onClick={loadPosts} disabled={loading}>
              {loading ? "⟳" : "↺ Refresh"}
            </button>
          </div>
          {loading && <div className="seo-loading">Loading posts…</div>}
          {!loading && posts.length === 0 && (
            <div className="seo-empty">No posts yet. Generate your first post above.</div>
          )}
          {posts.map(p => (
            <BlogPostCard
              key={p.id}
              post={p}
              onApprove={approve}
              onReject={reject}
              onPublish={publish}
              publishing={publishing === p.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
